import { Brands } from '../Brand';
import { useLayoutContext } from '../layout/Layout';
import { KeyValuePair } from './KeyValuePair';
import { STATE_LIST_ITEMS } from './ListItemHelper';
import { useGraphQL } from './useGraphQL';
import { MaskType } from './Textbox';

interface ILocalization {
    checking: string;
    company: string;
    customerRelationsFax: string;
    customerRelationsPhone: string;
    customerServiceEmail: string;
    customerServiceFax: string;
    customerServicePhone: string;
    consumerPrivacyEmail: string;
    consumerPrivacyFax: string;
    consumerPrivacyPhone: string;
    incomeVerificationEmail: string;
    isAvioCredit: boolean;
    isCashCentral: boolean;
    isSpeedyCash: boolean;
    isTitleMax: boolean;
    next: string;
    privacyPolicyUrl: string;
    shortDateFormat: string;
    signIn: string;
    signOut: string;
    socialSecurityNumber: string;
    state: string;
    stateListItems: Readonly<KeyValuePair<string, string>[]>;
    nonRestrictedStateListItems: Readonly<KeyValuePair<string, string>[]>;
    zip: string;
    zipMask: MaskType;
}

const USA: ILocalization = {
    checking: 'Checking',
    company: '',
    customerRelationsFax: '',
    customerRelationsPhone: '',
    customerServiceEmail: '',
    customerServiceFax: '',
    customerServicePhone: '',
    consumerPrivacyEmail: '',
    consumerPrivacyFax: '',
    consumerPrivacyPhone: '',
    incomeVerificationEmail: '',
    isAvioCredit: false,
    isCashCentral: false,
    isSpeedyCash: false,
    isTitleMax: false,
    next: 'Next',
    privacyPolicyUrl: 'https://disclosures.ccffamilyofbrands.com/ccf/fed-privacy-notice',
    shortDateFormat: 'M/d/yyyy',
    signIn: 'Sign in',
    signOut: 'Sign out',
    socialSecurityNumber: 'Social Security Number',
    state: 'State',
    stateListItems: STATE_LIST_ITEMS,
    nonRestrictedStateListItems: [],
    zip: 'Zip',
    zipMask: 'zip-code',
};

export function useLocalization() {
    const { model: layoutViewModel, brand } = useLayoutContext();
    const client = useGraphQL();

    let currentBrand = USA;
    let nonRestrictedStates = STATE_LIST_ITEMS;
    const cachedStates = sessionStorage.getItem("nonRestrictedStates");

    switch (brand) {
        case Brands.SpeedyCash:
            if (cachedStates) {
                nonRestrictedStates = JSON.parse(cachedStates);
            } else {
                client.getNonRestrictedStates().then((response) => {
                    nonRestrictedStates = response?.data?.getNonRestrictedStates ?? STATE_LIST_ITEMS;
                    sessionStorage.setItem('nonRestrictedStates', JSON.stringify(nonRestrictedStates));
                });
            }

            USA.isSpeedyCash = true;
            USA.company = layoutViewModel?.isRapidCash ? 'Rapid Cash' : 'Speedy Cash';
            USA.customerRelationsFax = '1-316-771-8801';
            USA.customerRelationsPhone = '1-800-856-2911';
            USA.customerServiceEmail = 'customerservice@speedycash.com';
            USA.customerServiceFax = '1-888-333-0568';
            USA.customerServicePhone = '1-888-333-1360';
            USA.consumerPrivacyEmail = 'privacy@speedycash.com';
            USA.incomeVerificationEmail = 'Verify@SpeedyCash.com';
            USA.privacyPolicyUrl = 'https://disclosures.ccffamilyofbrands.com/ccf/fed-privacy-notice';
            USA.nonRestrictedStateListItems = nonRestrictedStates;
            currentBrand = USA;
            break;
        case Brands.TitleMax:
            if (cachedStates) {
                nonRestrictedStates = JSON.parse(cachedStates);
            } else {
                client.getNonRestrictedStates().then((response) => {
                    nonRestrictedStates = response?.data?.getNonRestrictedStates ?? STATE_LIST_ITEMS;
                    sessionStorage.setItem('nonRestrictedStates', JSON.stringify(nonRestrictedStates));
                });
            }

            USA.isTitleMax = true;
            USA.company = 'TitleMax';
            USA.customerRelationsFax = '';
            USA.customerRelationsPhone = '1-855-959-8491';
            USA.customerServiceEmail = 'CustomerService@alerts.titlemax.com';
            USA.customerServiceFax = '';
            USA.customerServicePhone = '1-888-869-4522';
            USA.consumerPrivacyEmail = 'privacy@alerts.titlemax.com';
            USA.incomeVerificationEmail = 'Verify@alerts.titlemax.com';
            USA.privacyPolicyUrl = 'https://disclosures.ccffamilyofbrands.com/ccf/fed-privacy-notice';
            USA.nonRestrictedStateListItems = nonRestrictedStates;
            currentBrand = USA;
            break;
        case Brands.AvioCredit:
            if (cachedStates) {
                nonRestrictedStates = JSON.parse(cachedStates);
            } else {
                client.getNonRestrictedStates().then((response) => {
                    nonRestrictedStates = response?.data?.getNonRestrictedStates ?? STATE_LIST_ITEMS;
                    sessionStorage.setItem('nonRestrictedStates', JSON.stringify(nonRestrictedStates));
                });
            }

            USA.isAvioCredit = true;
            USA.company = 'Avío Credit';
            USA.customerRelationsFax = '';
            USA.customerRelationsPhone = '1-844-880-8655';
            USA.customerServiceEmail = 'customerservice@aviocredit.com';
            USA.customerServiceFax = '';
            USA.customerServicePhone = '1-888-638-2846';
            USA.consumerPrivacyEmail = 'privacy@aviocredit.com';
            USA.incomeVerificationEmail = 'applications@aviocredit.com';
            USA.privacyPolicyUrl = 'https://disclosures.ccffamilyofbrands.com/ccf/fed-privacy-notice';
            USA.nonRestrictedStateListItems = nonRestrictedStates;
            currentBrand = USA;
            break;
        case Brands.CashCentral:
            if (cachedStates) {
                nonRestrictedStates = JSON.parse(cachedStates);
            } else {
                client.getNonRestrictedStates().then((response) => {
                    nonRestrictedStates = response?.data?.getNonRestrictedStates ?? STATE_LIST_ITEMS;
                    sessionStorage.setItem('nonRestrictedStates', JSON.stringify(nonRestrictedStates));
                });
            }

            USA.isCashCentral = true;
            USA.company = 'Cash Central';
            USA.customerRelationsFax = '';
            USA.customerRelationsPhone = '1-844-259-3050';
            USA.customerServiceEmail = 'customerservice@info.cashcentral.com';
            USA.customerServiceFax = '';
            USA.customerServicePhone = '1-800-460-4305';
            USA.consumerPrivacyEmail = 'privacy@info.cashcentral.com';
            USA.incomeVerificationEmail = 'applications@info.cashcentral.com';
            USA.privacyPolicyUrl = 'https://disclosures.ccffamilyofbrands.com/ccf/fed-privacy-notice';
            USA.nonRestrictedStateListItems = nonRestrictedStates;
            USA.next = 'Continue';
            currentBrand = USA;
            break;
        default:
            break;
    }

    return currentBrand;
}
